import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

import {
  isAdmin,
  getAvailableGroupMembers,
  getGroupMemberId,
  getGroupId,
} from 'store/session/selectors';

import { isDigitalQuoteDomain } from 'utils/siteUtils';

import { DIFFERENT_ORGANIZATION_PATH_NAME, ORG_PARAM } from './data/constants';

const useRedirectToDifferentOrganizationPage = () => {
  /*
  Detects the ORG_PARAM in a users url and handles redirecting them to the different organization page if needed or removing the query param from the users url.

  Group members are redirected if:
  1. The requested group is a group that this user belongs too
  2. They are not currently active in this group

  Super users are redirected if:
  1. The requested group is not their current active group
  */
  const { pathname, search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const org = searchParams.get(ORG_PARAM);
  searchParams.delete(ORG_PARAM);
  const isSuperUser = useSelector(isAdmin);
  const availableGroupMembers = useSelector(getAvailableGroupMembers);
  const activeGroupMemberId = useSelector(getGroupMemberId);
  const history = useHistory();
  const currentGroupLocator = useSelector(getGroupId);

  return useMemo(() => {
    if (
      org &&
      pathname !== DIFFERENT_ORGANIZATION_PATH_NAME &&
      !isDigitalQuoteDomain()
    ) {
      // preserve the users desired url destination
      const nextQueryParams = searchParams.toString();
      const nextLink = nextQueryParams
        ? `${pathname}?${nextQueryParams}`
        : pathname;

      if (!isSuperUser) {
        // for non-super users we need to validate they are being redirected to a valid group member
        const requestedGroupMember = availableGroupMembers.find(
          (groupMember) => groupMember?.group?.locator === org,
        );
        if (
          requestedGroupMember &&
          requestedGroupMember?.id !== activeGroupMemberId
        ) {
          // the user needs to be redirected to the different organization page
          history.push(
            `${DIFFERENT_ORGANIZATION_PATH_NAME}?org=${org}&next=${encodeURIComponent(
              nextLink,
            )}`,
          );
        } else {
          // replace the current url without the org parameter
          history.replace(nextLink);
        }
      } else {
        // Redirect super users if the org param is not for their current group
        if (org !== currentGroupLocator) {
          history.push(
            `${DIFFERENT_ORGANIZATION_PATH_NAME}?org=${org}&next=${encodeURIComponent(
              nextLink,
            )}`,
          );
        } else {
          // replace the current url without the org parameter
          history.replace(nextLink);
        }
      }
      return false;
    }
    return true;
  }, [
    org,
    activeGroupMemberId,
    availableGroupMembers,
    isSuperUser,
    pathname,
    history,
    searchParams,
    currentGroupLocator,
  ]);
};

// useRedirectToDifferentOrganizationPage will modify the URL and validate the orginization
// this causes it to render twice (once with the org in the URL and once without)
// this wrapper makes it only load once when it returns true
const RedirectWrapper = ({ children }) => {
  const isDoneRendering = useRedirectToDifferentOrganizationPage();
  if (isDoneRendering) {
    return children;
  } else {
    return null;
  }
};

export default RedirectWrapper;
